import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  CLARIFICATION_PROGRESS,
  CLARIFICATION_STATUS,
} from '../../../shared/offer-domain/clarification';

const toStatusNumber = value => {
  if (value === null || value === undefined || value === '') return 0;
  const key = Number(value);
  return Number.isNaN(key) ? 0 : key;
};

const useClarificationProgressActions = ({ idOffer } = {}) => {
  const [savingAction, setSavingAction] = useState(null);

  const saveProgress = useCallback(
    async ({
      statusClarification,
      statusClarificationProgress,
      actionKey = 'sendClarification',
      successMessage = 'Clarification sent successfully.',
    } = {}) => {
      if (!idOffer) {
        toast.error('Offer ID is required.');
        return false;
      }

      const token = getAuthToken();
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }

      setSavingAction(actionKey);
      try {
        await axios.post(
          `${api.API_URL}/procurement/TenderEvaluationClarificationSaveProgress`,
          {
            idOffer,
            statusClarification,
            statusClarificationProgress,
          },
          {
            headers: {
              Accept: '*/*',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(successMessage);
        return true;
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to save clarification progress.';
        toast.error(message);
        return false;
      } finally {
        setSavingAction(null);
      }
    },
    [idOffer]
  );

  const sendClarification = useCallback(
    status => {
      const key = toStatusNumber(status);
      const fromPanitia = key === CLARIFICATION_STATUS.WAITING_PANITIA;
      return saveProgress({
        statusClarification: CLARIFICATION_STATUS.WAITING_DMUT,
        statusClarificationProgress: fromPanitia
          ? CLARIFICATION_PROGRESS.WAITING_DMUT
          : CLARIFICATION_PROGRESS.OPEN,
        actionKey: 'sendClarification',
        successMessage: 'Clarification sent successfully.',
      });
    },
    [saveProgress]
  );

  const submitIcClarification = useCallback(
    () =>
      saveProgress({
        statusClarification: CLARIFICATION_STATUS.WAITING_PANITIA,
        statusClarificationProgress: CLARIFICATION_PROGRESS.WAITING_PANITIA,
        actionKey: 'submitClarification',
        successMessage: 'Clarification submitted successfully.',
      }),
    [saveProgress]
  );

  const noClarificationNeeded = useCallback(async () => {
    if (!idOffer) {
      toast.error('Offer ID is required.');
      return false;
    }

    const token = getAuthToken();
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return false;
    }

    setSavingAction('closeClarification');
    try {
      await axios.post(
        `${api.API_URL}/procurement/TenderEvaluationClarificationNoClarification`,
        { idOffer },
        {
          headers: {
            Accept: '*/*',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success('Clarification closed successfully.');
      return true;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to close clarification.';
      toast.error(message);
      return false;
    } finally {
      setSavingAction(null);
    }
  }, [idOffer]);

  return {
    savingAction,
    isSaving: !!savingAction,
    sendClarification,
    submitIcClarification,
    closeClarification: noClarificationNeeded,
  };
};

export default useClarificationProgressActions;
